import React, { useState, useEffect, useCallback } from 'react';
import { Link2, AlertCircle, BarChart3, TrendingUp, X } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../../contexts/AppProvider';
import API_CONFIG from '../../config/api';

const BASE_URL = API_CONFIG.BASE_URL;

function SingleStatComponent({ value, description }) {
  return (
	<div 
	  className="p-6 border shadow-sm transition-all duration-200 hover:shadow-md"
	  style={{ 
		backgroundColor: 'var(--color-surface)',
		borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)'
	  }}
	>
	  <div className="flex flex-col items-center justify-center text-center">
		<div 
		  className="text-4xl font-bold mb-2"
		  style={{ color: 'var(--color-primary)' }}
		>
		  {value}
		</div>
		<div 
		  className="text-sm font-medium"
		  style={{ color: 'var(--color-muted)' }}
		>
		  {description}
		</div>
	  </div>
	</div>
  );
}

function VerticalBarComponent({ description, data }) {
  return (
	<div 
	  className="p-6 border shadow-sm"
	  style={{ 
		backgroundColor: 'var(--color-surface)',
		borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)'
	  }}
	>
	  <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text)' }}>
		{description}
	  </h3>
	  <div className="flex items-end justify-around gap-4 h-64">
		{data.map((group, index) => {
		  const donePercent = (group.completed / group.total) * 100;
		  const notDonePercent = (group.uncompleted / group.total) * 100;
		  
		return (
			<div key={index} className="flex flex-col items-center flex-1">
				{/* Completed number label */}
				<div
					className="text-xs mb-1 font-medium"
					style={{ color: 'var(--color-muted)' }}
				>
					<strong>{group.completed} / {group.total} ({donePercent}%)</strong>
				</div>
				<div className="w-full relative" style={{ minHeight: '210px' }}>
					<div 
					className="absolute bottom-0 w-full  transition-all duration-300"
					style={{ 
						height: `${donePercent}%`,
						backgroundColor: 'var(--color-primary)',
						opacity: 0.8
					}}
					/>
					<div 
					className="absolute w-full  transition-all duration-300"
					style={{ 
						bottom: `${donePercent}%`,
						height: `${notDonePercent}%`,
						backgroundColor: 'var(--color-muted)',
						opacity: 0.5
					}}
					/>
				</div>
				<div className="mt-2 text-center">
					<div className="text-xs font-semibold" style={{ color: 'var(--color-text)' }}>
					{group.group_name}
					</div>

				</div>
			</div>
		);
		})}
	  </div>
	  <div className="flex justify-center gap-6 mt-4 text-xs">
		<div className="flex items-center gap-2">
		  <div 
			className="w-3 h-3 rounded"
			style={{ backgroundColor: 'var(--color-primary)', opacity: 0.8 }}
		  />
		  <span style={{ color: 'var(--color-text)' }}>Completed</span>
		</div>
		<div className="flex items-center gap-2">
		  <div 
			className="w-3 h-3 rounded"
			style={{ backgroundColor: 'var(--color-muted)', opacity: 0.5 }}
		  />
		  <span style={{ color: 'var(--color-text)' }}>Uncompleted</span>
		</div>
	  </div>
	</div>
  );
}

export default function LinkGeneratorFeature({ featureName }) {
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [linkData, setLinkData] = useState(null);
	const { tenantInfos, t } = useApp();
	
	// Form state
	const [count, setCount] = useState(1);
	const [tags, setTags] = useState([]);
	const [tagInput, setTagInput] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	const fetchFeatureData = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const tenant_id = tenantInfos.interfaceId;
			const access_token = tenantInfos.accessToken;
			const api_url = `${BASE_URL}/cui_portal/${tenant_id}/${access_token}/features/get_data/${featureName}`;
			
			const result = await fetch(api_url);
			
			if (result.status === 404) {
				setError({statusCode: 404, detail: "Feature data not found"});
				return;
			}

			if (result.status === 401) {
				setError({statusCode: 401, detail: "You are not authorized to access this feature.\nPlease contact your administrator"});
				return;
			}

			if (!result.ok) {
				setError({statusCode: 500, detail: "Unexpected error happened"});
				return;
			}

			const response = await result.json();
			if (response.success && response.data) {
				setLinkData(response.data);
				setError(null);
			} else if (response.error) {
				setError({statusCode: response.error.status_code,detail: response.error.detail || response.error.message});
			} else {
				setError({statusCode: 422,detail: "Unexpected data structure"});
			}
		} catch (err) {
			console.error("Error fetching feature data:", err);
			setError({
			statusCode: 500,
			detail: "Failed to fetch feature data"
			});
		} finally {
			setIsLoading(false);
		}
	}, [featureName, tenantInfos]);

	useEffect(() => {
		fetchFeatureData();
	}, [fetchFeatureData]);

	const handleAddTag = (e) => {
		if (e.key === 'Enter' && tagInput.trim()) {
			e.preventDefault();
			if (!tags.includes(tagInput.trim())) {
				setTags([...tags, tagInput.trim()]);
			}
			setTagInput('');
		}
	};

	const handleRemoveTag = (tagToRemove) => {
		setTags(tags.filter(tag => tag !== tagToRemove));
	};

	const handleSubmit = async () => {
		setIsSubmitting(true);

		try {
			// Ensure tags exist
			if (!tags || tags.length === 0) {
				toast.error('🖐️ At least one tag is needed to generate links');
				return;
			}

			// Ensure client can download files
			if (!window || !window.URL || !window.Blob) {
				toast.error('⚠️ File download is not supported in this environment.');
				return;
			}

			// Construct API URL
			const tenant_id = tenantInfos.interfaceId;
			const access_token = tenantInfos.accessToken;
			const api_url = `${BASE_URL}/cui_portal/${tenant_id}/${access_token}/features/handle_action/${featureName}`;

			// Make request
			const response = await fetch(api_url, {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify({ count, tags })
			});
			// Handle known error cases
			if (response.status === 404) {
				setError({statusCode: 404,detail: "Feature data not found"});
				return;
			}
			if (response.status === 401) {
				setError({statusCode: 401,detail: "You are not authorized to access this feature.\nPlease contact your administrator"});
				return;
			}
			if (!response.ok) {
				setError({statusCode: 500,detail: "Unexpected error happened"});
				return;
			}

			// Parse response JSON
			const result = await response.json();
			if (result.success && result.data) {
				// Build CSV content
				const { base_url, survey_id, tokens } = result.data;

				const formatDate = (dateString) => {
					const d = new Date(dateString);
					const yyyy = d.getFullYear();
					const mm = String(d.getMonth() + 1).padStart(2, "0");
					const dd = String(d.getDate()).padStart(2, "0");
					const hh = String(d.getHours()).padStart(2, "0");
					const min = String(d.getMinutes()).padStart(2, "0");
					return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
				};

				const header = "Link,Valid from,Valid until";
				const rows = tokens.map(t => {
					const link = `${base_url}/${survey_id}/${t.token}`;
					const validFrom = formatDate(t.valid_from);
					const validUntil = formatDate(t.valid_until);
					return `${link},${validFrom},${validUntil}`;
				});

				const csvContent = [header, ...rows].join("\n");

				// Create downloadable CSV
				const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				a.download = `survey-links-${new Date().toISOString()}.csv`;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				window.URL.revokeObjectURL(url);

				// Reset form
				setCount(1);
				setTags([]);

				// Refresh data
				fetchFeatureData();

				toast.success("✅ Links generated and CSV downloaded successfully!");
				setError(null);
			} else if (result.error) {
				setError({statusCode: result.error.status_code,detail: result.error.detail || result.error.message});
			} else {
				setError({statusCode: 422,detail: "Unexpected data structure"});
			}

			
		} catch (err) {
			console.error("Error generating links:", err);
			setError({statusCode: 500,detail: "Unexpected error happened"});
		} finally {
			setIsSubmitting(false);
		}
	};


	// Loading state
	if (isLoading) {
		return (
			<div className="max-w-7xl mx-auto px-6 py-12">
			<div className="flex items-center justify-center min-h-[400px]">
				<div className="text-center">
				<div 
					className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
					style={{ borderColor: 'var(--color-primary)' }}
				/>
				<p style={{ color: 'var(--color-muted)' }}>Loading link data...</p>
				</div>
			</div>
			</div>
		);
	}

	// Error state
	if (error) {
		return (
			<div className="max-w-7xl mx-auto px-6 py-12">
			<div className="flex items-center justify-center min-h-[400px]">
				<div 
				className="p-6 rounded-lg border"
				style={{ 
					backgroundColor: 'color-mix(in srgb, red 10%, var(--color-surface))',
					borderColor: 'color-mix(in srgb, red 30%, transparent)'
				}}
				>
				<div className="flex items-start gap-3">
					<AlertCircle className="h-6 w-6 flex-shrink-0" style={{ color: 'red' }} />
					<div>
					<h3 className="font-semibold mb-1" style={{ color: 'var(--color-text)' }}>
						Error {error.statusCode}
					</h3>
					<p style={{ color: 'var(--color-text)', whiteSpace: 'pre-line' }}>
						{error.detail}
					</p>
					</div>
				</div>
				</div>
			</div>
			</div>
		);
	}


  // Main content
  return (
	<div className="mx-auto px-6 py-8">
	  {/* Split Layout */}
	  <div className="grid grid-cols-1 lg:grid-cols-[2fr_auto_1fr] gap-8">
		
		{/* Left Side - Statistics */}
		<div className="space-y-6">
		  {/* Header */}
		  <div className="flex items-end gap-2">
			<BarChart3 className="h-8 w-8 mb-1" style={{ color: 'var(--color-primary)' }} />
			<h1 className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
			  {t("linkStatsTitle")}
			</h1>
		  </div>

		  {/* Single Stats Row */}
		  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
			<SingleStatComponent 
			  value={linkData.total_tokens} 
			  description="Total Links"
			/>
			<SingleStatComponent 
			  value={linkData.active_tokens} 
			  description="Active Links"
			/>
			<SingleStatComponent 
			  value={linkData.completed_tokens} 
			  description="Completed"
			/>
		  </div>

		  {/* Bar Chart Row */}
		  <VerticalBarComponent 
			description={t("linkByGroup")}
			data={linkData.group_data}
		  />
		</div>

		{/* Separator */}
		<div className="hidden lg:block w-px" style={{ backgroundColor: 'var(--color-muted)', opacity: 0.3 }} />

		{/* Right Side - Link Generator Form */}
		<div className="space-y-4">
		<div className="flex items-end gap-2">
			<Link2 className="h-8 w-8 " style={{ color: 'var(--color-primary)' }} />
			<h1 className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
			{t("linkGeneratorTitle")}
			</h1>
		  </div>

		  <div className="flex flex-col">
			<div 
			  className="p-6  border shadow-sm mt-2"
			  style={{ 
				backgroundColor: 'var(--color-surface)',
				borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)'
			  }}
			>
			  <h2 className="text-xl font-semibold mb-6 flex items-center gap-2" style={{ color: 'var(--color-text)' }}>
				{t("generateNewLinks")}
			  </h2>

			  <div className="space-y-6">
				{/* Count Input */}
				<div>
				  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
					{t("numerOfLinks")}
				  </label>
				  <input
					type="number"
					min="1"
					max="2500"
					value={count}
					onChange={(e) => setCount(Math.min(2500, Math.max(1, parseInt(e.target.value) || 1)))}
					className="w-full px-4 py-2 rounded border focus:outline-none focus:ring-2"
					style={{ 
					  backgroundColor: 'var(--color-bg)',
					  borderColor: 'var(--color-muted)',
					  color: 'var(--color-text)'
					}}
					onFocus={(e) => {
					  e.target.style.borderColor = 'var(--color-primary)';
					}}
					onBlur={(e) => {
					  e.target.style.borderColor = 'var(--color-muted)';
					}}
				  />
				</div>

				{/* Tags Input */}
				<div>
				  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
					Tags
				  </label>
				  <input
					type="text"
					value={tagInput}
					onChange={(e) => setTagInput(e.target.value)}
					onKeyDown={handleAddTag}
					placeholder="Type a tag and press Enter"
					className="w-full px-4 py-2 rounded border focus:outline-none focus:ring-2"
					style={{ 
					  backgroundColor: 'var(--color-bg)',
					  borderColor: 'var(--color-muted)',
					  color: 'var(--color-text)'
					}}
					onFocus={(e) => {
					  e.target.style.borderColor = 'var(--color-primary)';
					}}
					onBlur={(e) => {
					  e.target.style.borderColor = 'var(--color-muted)';
					}}
				  />
				  
				  {/* Tag Display */}
				  {tags.length > 0 && (
					<div className="flex flex-wrap gap-2 mt-3">
					  {tags.map((tag, index) => (
						<span
						  key={index}
						  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm"
						  style={{ 
							backgroundColor: 'color-mix(in srgb, var(--color-primary) 20%, transparent)',
							color: 'var(--color-text)'
						  }}
						>
						  {tag}
						  <button
							type="button"
							onClick={() => handleRemoveTag(tag)}
							className="hover:opacity-70 transition-opacity"
						  >
							<X className="h-3 w-3" />
						  </button>
						</span>
					  ))}
					</div>
				  )}
				</div>

				{/* Submit Button */}
				<button
				  onClick={handleSubmit}
				  disabled={isSubmitting}
				  className="w-full px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
				  style={{
					backgroundColor: 'var(--color-primary)',
					color: 'var(--color-text-contrast)'
				  }}
				  onMouseEnter={(e) => {
					if (!isSubmitting) {
					  e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)';
					}
				  }}
				  onMouseLeave={(e) => {
					e.currentTarget.style.backgroundColor = 'var(--color-primary)';
				  }}
				>
				  {isSubmitting ? 'Generating...' : t("generateButton")}
				</button>
			  </div>

			  {/* Info Text */}
			  <div 
				className="mt-6 p-3 rounded border text-xs"
				style={{ 
				  backgroundColor: 'color-mix(in srgb, var(--color-primary) 5%, var(--color-surface))',
				  borderColor: 'color-mix(in srgb, var(--color-primary) 20%, transparent)',
				  color: 'var(--color-muted)'
				}}
			  >
				{t("linkGenratorText")}
			  </div>
			</div>
		  </div>
		</div>
	  </div>
	</div>
  );
}